import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Plus, Trash2, HardHat, Package, FileBarChart, LayoutDashboard,
  Download, Loader2, Users, Clock, Wallet, TrendingUp, Pencil, Layers, FileSpreadsheet, FileText, Eye, Upload, FileUp, Paperclip, Camera, Image as ImageIcon, Wrench, FileArchive, Search,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { api, API, eur, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import NuovaLavorazioneButton from "@/components/NuovaLavorazioneButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { printPdfBlobDirect } from "@/lib/directPrint";
import { openAttachmentViewer } from "@/lib/attachmentViewer";

const CHART_COLORS = ["#FF5A00", "#3B82F6", "#10B981", "#F59E0B", "#0A2540"];
const STATI = ["In corso", "In pausa", "Completato"];

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">{label}</p>
        <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-secondary"}`} />
      </div>
      <p className="font-heading text-2xl font-bold tracking-tight">{value}</p>
    </div>
  );
}

function DatePicker({ value, onChange, testid, placeholder = "Seleziona data", max }) {
  const valore = value instanceof Date && !Number.isNaN(value.getTime())
    ? format(value, "yyyy-MM-dd")
    : "";

  return (
    <Input
      type="date"
      data-testid={testid}
      aria-label={placeholder}
      title={`${placeholder}: puoi digitare la data oppure usare il calendario`}
      value={valore}
      max={max}
      onChange={(event) => {
        const nuovoValore = event.target.value;
        onChange(
          nuovoValore
            ? new Date(`${nuovoValore}T00:00:00`)
            : undefined
        );
      }}
    />
  );
}

function costoOperaioAllaData(operaio, data) {
  if (!operaio) return "";
  const giorno = data instanceof Date && !Number.isNaN(data.getTime())
    ? format(data, "yyyy-MM-dd")
    : String(data || "");
  const storico = Array.isArray(operaio.costo_orario_storico)
    ? operaio.costo_orario_storico
        .filter((voce) => String(voce.dal || "") <= giorno)
        .sort((a, b) => String(a.dal || "").localeCompare(String(b.dal || "")))
    : [];
  const tariffa = storico.length ? storico[storico.length - 1].costo_orario : operaio.costo_orario;
  return String(Number(tariffa) || 0);
}

export default function CantiereDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cantiere, setCantiere] = useState(null);
  const [mano, setMano] = useState([]);
  const [mat, setMat] = useState([]);
  const [report, setReport] = useState(null);
  const [reportDate, setReportDate] = useState(new Date());
  const [downloading, setDownloading] = useState(false);
  const [downloadingXls, setDownloadingXls] = useState(false);
  const [lavDetail, setLavDetail] = useState(null);
  const [uploadingBolla, setUploadingBolla] = useState(false);
  const [foto, setFoto] = useState([]);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotoSelezionate, setFotoSelezionate] = useState([]);
  const [scaricandoFoto, setScaricandoFoto] = useState(false);
  const [incassoOpen, setIncassoOpen] = useState(false);
  const [incassoEditId, setIncassoEditId] = useState(null);
  const [incassoForm, setIncassoForm] = useState({
    data_incasso: format(new Date(), "yyyy-MM-dd"),
    importo: "",
    modalita: "bonifico",
    riferimento: "",
    note: "",
    azienda_id: "",
    azienda_nome: "",
  });

  const [manoOpen, setManoOpen] = useState(false);
  const [matOpen, setMatOpen] = useState(false);
  const [ddtImportOpen, setDdtImportOpen] = useState(false);
  const [ddtImportFile, setDdtImportFile] = useState(null);
  const [ddtPreview, setDdtPreview] = useState(null);
  const [ddtAnalyzing, setDdtAnalyzing] = useState(false);
  const [ddtImporting, setDdtImporting] = useState(false);
  const [manoEditId, setManoEditId] = useState(null);
  const [matEditId, setMatEditId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const lavoriExtraRef = useRef(null);
  const [editForm, setEditForm] = useState({
    nome: "",
    cliente: "",
    indirizzo: "",
    data_inizio: "",
    budget: "",
    lavori_extra: [],
    stato: "In corso",
    completed_at: "",
    descrizione: "",
    fuori_budget: false,
  });
  const [manoForm, setManoForm] = useState({ operaio: "", operaio_id: null, ruolo: "", azienda: "", data: new Date(), ore: "8", costo_orario: "", lavorazione_id: "__none__" });
  const [operaiCatalogo, setOperaiCatalogo] = useState([]);
  const [disponibilitaOperai, setDisponibilitaOperai] = useState({});
  const [disponibilitaOperaiLoading, setDisponibilitaOperaiLoading] = useState(false);
  const [fornitoriAnagrafica, setFornitoriAnagrafica] = useState([]);
  const [aziendeAnagrafica, setAziendeAnagrafica] = useState([]);
  const [matForm, setMatForm] = useState({ nome: "", numero_ddt: "", costo_totale: "", fornitore: "", azienda_id: "", azienda_nome: "", data_acquisto: new Date(), lavorazione_id: "__none__", allegato_path: null, allegato_nome: null });

  const [attrezzi, setAttrezzi] = useState([]);
  const [catalogoAttrezzi, setCatalogoAttrezzi] = useState([]);
  const [attrezzoOpen, setAttrezzoOpen] = useState(false);
  const [attrezzoEditId, setAttrezzoEditId] = useState(null);
  const [attrezzoForm, setAttrezzoForm] = useState({
    catalogo_id: "",
    azienda_id: "",
    azienda_nome: "",
    data_utilizzo: format(new Date(), "yyyy-MM-dd"),
    ore_utilizzate: "",
    lavorazione_id: "__none__",
  });

  const [attrezzoNomeFilter, setAttrezzoNomeFilter] = useState("Tutti");
  const [attrezzoAziendaFilter, setAttrezzoAziendaFilter] = useState("Tutte");
  const [attrezzoGiornoFilter, setAttrezzoGiornoFilter] = useState("");
  const [attrezzoMeseFilter, setAttrezzoMeseFilter] = useState("");
  const [lavorazioni, setLavorazioni] = useState([]);
  const [lavOpen, setLavOpen] = useState(false);
  const [lavEditId, setLavEditId] = useState(null);
  const [lavForm, setLavForm] = useState({ nome: "", descrizione: "", stato: "In corso" });
  const [manoLavFilter, setManoLavFilter] = useState("Tutte");
  const [manoOperaioFilter, setManoOperaioFilter] = useState("Tutti");
  const [manoAziendaFilter, setManoAziendaFilter] = useState("Tutte");
  const [manoDataFilter, setManoDataFilter] = useState("");
  const [manoMeseFilter, setManoMeseFilter] = useState("");
  const [matLavFilter, setMatLavFilter] = useState("Tutte");
  const [matFornitoreFilter, setMatFornitoreFilter] = useState("Tutti");
  const [matSearchFilter, setMatSearchFilter] = useState("");
  const [matDataFilter, setMatDataFilter] = useState("");
  const [matMeseFilter, setMatMeseFilter] = useState("");
  const [selezioneLavorazioneCreata, setSelezioneLavorazioneCreata] = useState(null);

  useEffect(() => {
    const selezione = selezioneLavorazioneCreata;
    if (!selezione || !lavorazioni.some((voce) => voce.id === selezione.id)) return;
    if (selezione.sezione === "manodopera") {
      setManoForm((corrente) => ({ ...corrente, lavorazione_id: selezione.id }));
    } else if (selezione.sezione === "materiali") {
      setMatForm((corrente) => ({ ...corrente, lavorazione_id: selezione.id }));
    } else if (selezione.sezione === "attrezzature") {
      setAttrezzoForm((corrente) => ({ ...corrente, lavorazione_id: selezione.id }));
    } else if (selezione.sezione === "importazione-ddt") {
      aggiornaRigaDDT(selezione.indice, "lavorazione_id", selezione.id);
    }
    setSelezioneLavorazioneCreata(null);
  }, [lavorazioni, selezioneLavorazioneCreata]);

  const collegaLavorazioneCreata = (sezione, nuova, indice = null) => {
    if (!nuova?.id) return;
    setLavorazioni((correnti) => [...correnti.filter((voce) => voce.id !== nuova.id), nuova]);
    setSelezioneLavorazioneCreata({ sezione, indice, id: nuova.id });
  };

  const loadBase = useCallback(() => {
    api.get(`/cantieri/${id}`).then((r) => setCantiere(r.data)).catch(() => navigate("/"));
    api.get(`/cantieri/${id}/manodopera`).then((r) => setMano(r.data));
    api.get(`/cantieri/${id}/materiali`).then((r) => setMat(r.data));
    api.get(`/cantieri/${id}/attrezzi`).then((r) => setAttrezzi(r.data)).catch(() => setAttrezzi([]));
    api.get("/attrezzi-catalogo").then((r) => setCatalogoAttrezzi(r.data)).catch(() => setCatalogoAttrezzi([]));
    api.get("/gestionale/operai").then((r) => setOperaiCatalogo(Array.isArray(r.data) ? r.data : [])).catch(() => setOperaiCatalogo([]));
    api.get("/gestionale/anagrafiche/fornitori", { params: { include_archived: false } })
      .then((r) => {
        const elenco = Array.isArray(r.data) ? r.data : [];
        setFornitoriAnagrafica(
          elenco
            .filter((item) => !item.archived_at && item.nome)
            .sort((a, b) => a.nome.localeCompare(b.nome, "it"))
        );
      })
      .catch(() => setFornitoriAnagrafica([]));
    api.get("/gestionale/anagrafiche/aziende", { params: { include_archived: false } })
      .then((r) => {
        const aziende = (Array.isArray(r.data) ? r.data : [])
          .filter((x) => !x.archived_at)
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "it"));
        setAziendeAnagrafica(aziende);
        const aduea = aziende.find((azienda) =>
          String(azienda.nome || "").toLocaleLowerCase("it").replace(/[^a-z0-9]/g, "").includes("aduea")
        );
        if (aduea) {
          setMatForm((corrente) => corrente.azienda_id ? corrente : {
            ...corrente,
            azienda_id: aduea.id,
            azienda_nome: aduea.nome,
          });
        }
      })
      .catch(() => setAziendeAnagrafica([]));
    api.get("/attrezzi-catalogo").then((r) => setCatalogoAttrezzi(r.data)).catch(() => setCatalogoAttrezzi([]));
    api.get(`/cantieri/${id}/lavorazioni`).then((r) => setLavorazioni(r.data));
    api.get(`/cantieri/${id}/foto`).then((r) => setFoto(r.data));
  }, [id, navigate]);

  const loadReport = useCallback(() => {
    const d = reportDate ? format(reportDate, "yyyy-MM-dd") : undefined;
    api.get(`/cantieri/${id}/report`, { params: d ? { data: d } : {} }).then((r) => setReport(r.data));
  }, [id, reportDate]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadReport(); }, [loadReport, mano, mat]);

  useEffect(() => {
    if (!manoOpen || !(manoForm.data instanceof Date) || Number.isNaN(manoForm.data.getTime())) return undefined;
    let annullata = false;
    setDisponibilitaOperaiLoading(true);
    api.get("/manodopera/disponibilita", {
      params: {
        data: format(manoForm.data, "yyyy-MM-dd"),
        ...(manoEditId ? { exclude_id: manoEditId } : {}),
      },
    })
      .then((response) => {
        if (!annullata) setDisponibilitaOperai(response.data?.operai || {});
      })
      .catch(() => {
        if (!annullata) {
          setDisponibilitaOperai({});
          toast.error("Impossibile verificare le ore disponibili degli operai");
        }
      })
      .finally(() => {
        if (!annullata) setDisponibilitaOperaiLoading(false);
      });
    return () => { annullata = true; };
  }, [manoOpen, manoForm.data, manoEditId]);

  const disponibilitaSelezionato = manoForm.operaio_id
    ? (disponibilitaOperai[manoForm.operaio_id] || { ore_utilizzate: 0, ore_disponibili: 8 })
    : null;
  const operaiDisponibili = operaiCatalogo.filter((operaio) => {
    const disponibilita = disponibilitaOperai[operaio.id] || { ore_disponibili: 8 };
    return Number(disponibilita.ore_disponibili) > 0;
  });

  useEffect(() => {
    if (
      manoOpen && !manoEditId && !disponibilitaOperaiLoading && manoForm.operaio_id
      && disponibilitaOperai[manoForm.operaio_id]
      && Number(disponibilitaOperai[manoForm.operaio_id].ore_disponibili) <= 0
    ) {
      setManoForm((corrente) => ({
        ...corrente,
        operaio_id: null, operaio: "", ruolo: "", azienda: "", costo_orario: "", ore: "",
      }));
      toast.info("L'operaio selezionato ha già raggiunto 8 ore nella nuova data");
    }
  }, [disponibilitaOperai, disponibilitaOperaiLoading, manoEditId, manoForm.operaio_id, manoOpen]);

  const resetMano = () => { setManoEditId(null); setManoForm({ operaio: "", operaio_id: null, ruolo: "", azienda: "", data: new Date(), ore: "8", costo_orario: "", lavorazione_id: "__none__" }); };
  const openEditMano = (m) => {
    setManoEditId(m.id);
    setManoForm({ operaio: m.operaio, operaio_id: m.operaio_id || null, ruolo: m.ruolo || "", azienda: m.azienda || "", data: new Date(m.data), ore: String(m.ore), costo_orario: String(m.costo_orario), lavorazione_id: m.lavorazione_id || "__none__" });
    setManoOpen(true);
  };
  const addMano = async (e) => {
    e.preventDefault();
    if (!manoForm.operaio_id) {
      toast.error("Seleziona un operaio presente nell'anagrafica");
      return;
    }
    const oreRichieste = parseFloat(manoForm.ore) || 0;
    const oreDisponibili = Number(disponibilitaSelezionato?.ore_disponibili ?? 8);
    if (oreRichieste <= 0 || oreRichieste > oreDisponibili) {
      toast.error(`Puoi inserire al massimo ${oreDisponibili} ore per questo operaio nella data selezionata`);
      return;
    }
    try {
      const payload = {
        operaio: manoForm.operaio, operaio_id: manoForm.operaio_id, ruolo: manoForm.ruolo, azienda: manoForm.azienda,
        data: format(manoForm.data, "yyyy-MM-dd"),
        ore: parseFloat(manoForm.ore) || 0, costo_orario: parseFloat(manoForm.costo_orario) || 0,
        lavorazione_id: manoForm.lavorazione_id === "__none__" ? null : manoForm.lavorazione_id,
      };
      if (manoEditId) { await api.put(`/manodopera/${manoEditId}`, payload); toast.success("Presenza aggiornata"); }
      else { await api.post(`/cantieri/${id}/manodopera`, payload); toast.success("Presenza registrata"); }
      setManoOpen(false); resetMano(); loadBase();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const aziendaAduea = () => aziendeAnagrafica.find((azienda) =>
    String(azienda.nome || "").toLocaleLowerCase("it").replace(/[^a-z0-9]/g, "").includes("aduea")
  );
  const resetMat = () => {
    const azienda = aziendaAduea();
    setMatEditId(null);
    setMatForm({ nome: "", numero_ddt: "", costo_totale: "", fornitore: "", azienda_id: azienda?.id || "", azienda_nome: azienda?.nome || "", data_acquisto: new Date(), lavorazione_id: "__none__", allegato_path: null, allegato_nome: null });
  };
  const openEditMat = (m) => {
    const azienda = m.azienda_id ? aziendeAnagrafica.find((x) => x.id === m.azienda_id) : aziendaAduea();
    setMatEditId(m.id);
    setMatForm({ nome: m.nome, numero_ddt: m.numero_ddt || "", costo_totale: String(m.costo_totale ?? ""), fornitore: m.fornitore || "", azienda_id: m.azienda_id || azienda?.id || "", azienda_nome: m.azienda_nome || azienda?.nome || "", data_acquisto: new Date(m.data_acquisto), lavorazione_id: m.lavorazione_id || "__none__", allegato_path: m.allegato_path || null, allegato_nome: m.allegato_nome || null });
    setMatOpen(true);
  };
  const uploadBolla = async (file) => {
    if (!file) return;
    setUploadingBolla(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/bolla", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMatForm((f) => ({ ...f, allegato_path: data.path, allegato_nome: data.nome }));
      toast.success("Bolla caricata");
    } catch (err) { toast.error("Errore caricamento bolla"); }
    finally { setUploadingBolla(false); }
  };
  const bollaUrl = (p) => `${API}/uploads/bolla/${p}`;
  const materialeHaBolla = (materiale) => Boolean(
    materiale?.allegato_path || materiale?.magazzino_movement_id
  );
  const materialeBollaUrl = (materiale) => materiale?.magazzino_movement_id
    ? `${API}/gestionale/magazzino/movimenti/${materiale.magazzino_movement_id}/ddt.pdf`
    : bollaUrl(materiale?.allegato_path);
  const uploadFoto = async (selezione) => {
    const files = Array.from(selezione || []).filter((file) => file?.type?.startsWith("image/"));
    if (!files.length) return toast.error("Seleziona almeno una fotografia");
    setUploadingFoto(true);
    let caricate = 0;
    let errori = 0;
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads/bolla", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/cantieri/${id}/foto`, { path: data.path, nome: data.nome });
        caricate += 1;
      } catch (e) { errori += 1; }
    }
    if (caricate) toast.success(caricate === 1 ? "Foto aggiunta" : `${caricate} foto aggiunte`);
    if (errori) toast.error(`${errori} ${errori === 1 ? "foto non caricata" : "foto non caricate"}`);
    await loadBase();
    setUploadingFoto(false);
  };
  const delFoto = async (fid) => { await api.delete(`/foto/${fid}`); toast.success("Foto eliminata"); loadBase(); };
  const scaricaFotoSelezionate = async () => {
    const selezionate = foto.filter((item) => fotoSelezionate.includes(item.id));
    if (!selezionate.length) return toast.error("Seleziona almeno una foto");
    setScaricandoFoto(true);
    try {
      const singola = selezionate.length === 1;
      const risposta = singola
        ? await api.get(`/uploads/bolla/${selezionate[0].path}`, { responseType: "blob" })
        : await api.post(`/cantieri/${id}/foto/zip`, { ids: fotoSelezionate }, { responseType: "blob" });
      const collegamento = document.createElement("a");
      collegamento.href = URL.createObjectURL(risposta.data);
      collegamento.download = singola ? (selezionate[0].nome || "foto-cantiere.jpg") : `foto-${cantiere?.nome || "cantiere"}.zip`;
      document.body.appendChild(collegamento); collegamento.click(); collegamento.remove();
      URL.revokeObjectURL(collegamento.href);
      toast.success(singola ? "Foto scaricata" : `${selezionate.length} foto scaricate nello ZIP`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download non riuscito"); }
    finally { setScaricandoFoto(false); }
  };
  const addMat = async (e) => {
    e.preventDefault();
    if (aziendeAnagrafica.length > 0 && !matForm.azienda_id) {
      toast.error("Seleziona l'azienda a cui attribuire la spesa");
      return;
    }
    try {
      const payload = {
        nome: matForm.nome, numero_ddt: matForm.numero_ddt,
        costo_totale: parseFloat(matForm.costo_totale) || 0, fornitore: matForm.fornitore,
        azienda_id: matForm.azienda_id || null, azienda_nome: matForm.azienda_nome || "",
        data_acquisto: format(matForm.data_acquisto, "yyyy-MM-dd"),
        lavorazione_id: matForm.lavorazione_id === "__none__" ? null : matForm.lavorazione_id,
        allegato_path: matForm.allegato_path, allegato_nome: matForm.allegato_nome,
      };
      if (matEditId) { await api.put(`/materiali/${matEditId}`, payload); toast.success("Materiale aggiornato"); }
      else { await api.post(`/cantieri/${id}/materiali`, payload); toast.success("Materiale registrato"); }
      setMatOpen(false); resetMat(); loadBase();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const chiudiImportDDT = () => {
    setDdtImportOpen(false);
    setDdtImportFile(null);
    setDdtPreview(null);
  };

  const analizzaDDT = async () => {
    if (!ddtImportFile) {
      toast.error("Seleziona un PDF o una foto del DDT");
      return;
    }
    setDdtAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", ddtImportFile);
      const response = await api.post(
        `/cantieri/${id}/materiali/importa-ddt/anteprima`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const azienda = aziendaAduea();
      setDdtPreview({
        ...response.data,
        righe: (response.data?.righe || []).map((riga) => ({
          ...riga,
          azienda_id: riga.azienda_id || azienda?.id || "",
          azienda_nome: riga.azienda_nome || azienda?.nome || "",
        })),
      });
      if (!response.data?.righe?.length) toast.error("Nessun DDT riconosciuto nel documento");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || "Analisi DDT non riuscita"));
    } finally {
      setDdtAnalyzing(false);
    }
  };

  const aggiornaRigaDDT = (indice, campo, valore) => {
    setDdtPreview((corrente) => ({
      ...corrente,
      righe: corrente.righe.map((riga, posizione) =>
        posizione === indice ? { ...riga, [campo]: valore } : riga
      ),
    }));
  };

  const importaDDT = async () => {
    const righe = (ddtPreview?.righe || []).filter((riga) => !riga.duplicato);
    if (!righe.length) {
      toast.error("Non ci sono DDT nuovi da importare");
      return;
    }
    if (aziendeAnagrafica.length > 0 && righe.some((riga) => !riga.azienda_id)) {
      toast.error("Seleziona l'azienda a cui attribuire la spesa per ogni DDT");
      return;
    }
    setDdtImporting(true);
    try {
      const response = await api.post(`/cantieri/${id}/materiali/importa-ddt/conferma`, {
        token: ddtPreview.token,
        righe: righe.map((riga) => ({
          documento_id: riga.documento_id,
          numero_ddt: riga.numero_ddt || "",
          data_acquisto: riga.data_acquisto || "",
          fornitore: riga.fornitore || "",
          nome: riga.nome || `Materiali DDT ${riga.numero_ddt || ""}`,
          costo_totale: Number(riga.costo_totale) || 0,
          azienda_id: riga.azienda_id || null,
          azienda_nome: riga.azienda_nome || "",
          lavorazione_id: !riga.lavorazione_id || riga.lavorazione_id === "__none__" ? null : riga.lavorazione_id,
        })),
      });
      const duplicati = response.data?.duplicati?.length || 0;
      toast.success(`${response.data.importati} DDT importati${duplicati ? `, ${duplicati} duplicati ignorati` : ""}`);
      chiudiImportDDT();
      loadBase();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || "Importazione DDT non riuscita"));
    } finally {
      setDdtImporting(false);
    }
  };

  const resetAttrezzo = () => {
    setAttrezzoEditId(null);
    setAttrezzoForm({
      catalogo_id: "",
      azienda_id: "",
      azienda_nome: "",
      data_utilizzo: format(new Date(), "yyyy-MM-dd"),
      ore_utilizzate: "",
      lavorazione_id: "__none__",
    });
  };

  const openEditAttrezzo = (item) => {
    setAttrezzoEditId(item.id);
    setAttrezzoForm({
      catalogo_id: item.catalogo_id || "",
      azienda_id: item.azienda_id || "",
      azienda_nome: item.azienda_nome || item.azienda || "",
      data_utilizzo:
        item.data_utilizzo ||
        format(new Date(), "yyyy-MM-dd"),
      ore_utilizzate: String(item.ore_utilizzate || ""),
      lavorazione_id: item.lavorazione_id || "__none__",
    });
    setAttrezzoOpen(true);
  };

  const saveAttrezzo = async (e) => {
    e.preventDefault();

    if (!attrezzoForm.catalogo_id) {
      toast.error("Seleziona un attrezzo");
      return;
    }
    if (!attrezzoForm.azienda_id) {
      toast.error("Seleziona l'azienda a cui attribuire la spesa");
      return;
    }

    const payload = {
      catalogo_id: attrezzoForm.catalogo_id,
      azienda_id: attrezzoForm.azienda_id,
      azienda_nome: attrezzoForm.azienda_nome,
      data_utilizzo: attrezzoForm.data_utilizzo,
      ore_utilizzate:
        parseFloat(attrezzoForm.ore_utilizzate) || 0,
      lavorazione_id:
        attrezzoForm.lavorazione_id === "__none__"
          ? null
          : attrezzoForm.lavorazione_id,
    };

    try {
      if (attrezzoEditId) {
        await api.put(
          `/attrezzi/${attrezzoEditId}`,
          payload
        );
        toast.success("Utilizzo attrezzo aggiornato");
      } else {
        await api.post(
          `/cantieri/${id}/attrezzi`,
          payload
        );
        toast.success("Attrezzo assegnato al cantiere");
      }

      setAttrezzoOpen(false);
      resetAttrezzo();
      loadBase();
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail)
      );
    }
  };

  const deleteAttrezzo = async (item) => {
    const conferma = window.confirm(
      `Eliminare l'utilizzo dell'attrezzo "${item.attrezzo}"?`
    );

    if (!conferma) return;

    try {
      await api.delete(`/attrezzi/${item.id}`);
      toast.success("Utilizzo attrezzo eliminato");
      loadBase();
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail)
      );
    }
  };

  const attrezzoSelezionato = catalogoAttrezzi.find(
    (item) => item.id === attrezzoForm.catalogo_id
  );
  const attrezzatureUsate = Array.from(
    new Map(
      attrezzi
        .filter((item) => item.catalogo_id)
        .map((item) => [
          item.catalogo_id,
          {
            id: item.catalogo_id,
            nome: item.attrezzo,
            targa: item.targa || "",
          },
        ])
    ).values()
  ).sort((a, b) => a.nome.localeCompare(b.nome, "it"));

  const aziendeAttrezziFilter = [
    ...new Set(
      attrezzi
        .map((item) => item.azienda)
        .filter(Boolean)
    ),
  ].sort((a, b) => a.localeCompare(b, "it"));

  const attrezziView = attrezzi.filter((item) => {
    const matchAttrezzo =
      attrezzoNomeFilter === "Tutti" ||
      item.catalogo_id === attrezzoNomeFilter;

    const matchAzienda =
      attrezzoAziendaFilter === "Tutte" ||
      item.azienda === attrezzoAziendaFilter;

    const matchData = attrezzoGiornoFilter
      ? item.data_utilizzo === attrezzoGiornoFilter
      : attrezzoMeseFilter
        ? (item.data_utilizzo || "").startsWith(
            attrezzoMeseFilter
          )
        : true;

    return matchAttrezzo && matchAzienda && matchData;
  });

  const costoTotaleAttrezzi = attrezziView.reduce(
    (totale, item) =>
      totale +
      (Number(item.ore_utilizzate) || 0) *
      (Number(item.costo_orario) || 0),
    0
  );
  const resetLav = () => { setLavEditId(null); setLavForm({ nome: "", descrizione: "", stato: "In corso" }); };
  const openEditLav = (l) => { setLavEditId(l.id); setLavForm({ nome: l.nome, descrizione: l.descrizione || "", stato: l.stato || "In corso" }); setLavOpen(true); };
  const saveLav = async (e) => {
    e.preventDefault();
    try {
      if (lavEditId) { await api.put(`/lavorazioni/${lavEditId}`, lavForm); toast.success("Lavorazione aggiornata"); }
      else { await api.post(`/cantieri/${id}/lavorazioni`, lavForm); toast.success("Lavorazione creata"); }
      setLavOpen(false); resetLav(); loadBase();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delLav = async (lid) => { await api.delete(`/lavorazioni/${lid}`); toast.success("Lavorazione eliminata"); loadBase(); };
  const lavName = (lid) => lavorazioni.find((l) => l.id === lid)?.nome || "-";

  const openEditCantiere = () => {
    setEditForm({
      nome: cantiere.nome,
      cliente: cantiere.cliente || "",
      indirizzo: cantiere.indirizzo || "",
      data_inizio: cantiere.data_inizio || String(cantiere.created_at || "").slice(0, 10),
      budget: String(cantiere.budget || ""),
      lavori_extra: (cantiere.lavori_extra || []).map((extra) => ({
        descrizione: extra.descrizione || "",
        importo: String(extra.importo || ""),
      })),
      stato: cantiere.stato || "In corso",
      completed_at: String(cantiere.completed_at || "").slice(0, 10),
      descrizione: cantiere.descrizione || "",
      fuori_budget: Boolean(cantiere.fuori_budget),
    });
    setEditOpen(true);
  };
  const saveCantiere = async (e) => {
    e.preventDefault();
    try {
      const dataCompletamento = editForm.stato === "Completato"
        ? String(editForm.completed_at || "").slice(0, 10)
        : null;
      if (editForm.stato === "Completato" && !dataCompletamento) {
        toast.error("Indica la data di completamento");
        return;
      }
      const risposta = await api.put(`/cantieri/${id}`, {
        ...editForm,
        completed_at: dataCompletamento,
        budget: parseFloat(editForm.budget) || 0,
        lavori_extra: (editForm.lavori_extra || [])
          .filter((extra) =>
            extra.descrizione.trim() ||
            parseFloat(extra.importo) > 0
          )
          .map((extra) => ({
            descrizione: extra.descrizione.trim(),
            importo: parseFloat(extra.importo) || 0,
          })),
      });
      setCantiere(risposta.data);
      toast.success("Cantiere aggiornato");
      setEditOpen(false);
      loadBase();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delMano = async (itemId) => { await api.delete(`/manodopera/${itemId}`); toast.success("Voce eliminata"); loadBase(); };
  const delMat = async (itemId) => { await api.delete(`/materiali/${itemId}`); toast.success("Voce eliminata"); loadBase(); };

  const exportSectionPdf = async (
    sezione,
    nomeFile,
    righeVisualizzate
  ) => {
    try {
      const idsVisualizzati = (
        Array.isArray(righeVisualizzate)
          ? righeVisualizzate
          : []
      )
        .map((elemento) => elemento?.id)
        .filter(Boolean);

      const response = await api.get(
        `/cantieri/${id}/sezioni/${sezione}/report/pdf`,
        {
          params: {
            ids:
              idsVisualizzati.length > 0
                ? idsVisualizzati.join(",")
                : "__none__",
          },
          responseType: "blob",
        }
      );

      printPdfBlobDirect(new Blob([response.data], { type: "application/pdf" }));
    } catch (errore) {
      toast.error("Impossibile creare il report PDF");
    }
  };
  const exportPdf = async () => {
    setDownloading(true);
    try {
      const d = reportDate ? format(reportDate, "yyyy-MM-dd") : undefined;
      const res = await api.get(`/cantieri/${id}/report/pdf`, { params: d ? { data: d } : {}, responseType: "blob" });
      printPdfBlobDirect(new Blob([res.data], { type: "application/pdf" }));
    } catch (err) { toast.error("Errore export PDF"); }
    finally { setDownloading(false); }
  };

  const exportExcel = async () => {
    setDownloadingXls(true);
    try {
      const d = reportDate ? format(reportDate, "yyyy-MM-dd") : undefined;
      const res = await api.get(`/cantieri/${id}/report/excel`, { params: d ? { data: d } : {}, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rendiconto_${cantiere?.nome || "cantiere"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel esportato");
    } catch (err) { toast.error("Errore export Excel"); }
    finally { setDownloadingXls(false); }
  };

  const resetIncasso = () => {
    const azienda = aziendaAduea();
    setIncassoEditId(null);
    setIncassoForm({
      data_incasso: format(new Date(), "yyyy-MM-dd"),
      importo: "",
      modalita: "bonifico",
      riferimento: "",
      note: "",
      azienda_id: azienda?.id || "",
      azienda_nome: azienda?.nome || "",
    });
  };

  const nuovoIncasso = () => {
    resetIncasso();
    setIncassoOpen(true);
  };

  const modificaIncasso = (voce) => {
    setIncassoEditId(voce.id);
    setIncassoForm({
      data_incasso: voce.data_incasso || format(new Date(), "yyyy-MM-dd"),
      importo: String(voce.importo ?? ""),
      modalita: voce.modalita || "bonifico",
      riferimento: voce.riferimento || "",
      note: voce.note || "",
      azienda_id: voce.azienda_id || "",
      azienda_nome: voce.azienda_nome || "",
    });
    setIncassoOpen(true);
  };

  const salvaIncasso = async (event) => {
    event.preventDefault();
    if (aziendeAnagrafica.length > 0 && !incassoForm.azienda_id) {
      toast.error("Seleziona l'azienda che ha ricevuto l'incasso");
      return;
    }
    const payload = {
      ...incassoForm,
      importo: Number(incassoForm.importo) || 0,
    };
    try {
      if (incassoEditId) {
        await api.put(`/incassi-cantiere/${incassoEditId}`, payload);
        toast.success("Incasso aggiornato");
      } else {
        await api.post(`/cantieri/${id}/incassi`, payload);
        toast.success("Incasso registrato");
      }
      setIncassoOpen(false);
      resetIncasso();
      loadReport();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const eliminaIncasso = async (voce) => {
    if (!window.confirm(`Eliminare l'incasso di ${eur(voce.importo)}?`)) return;
    try {
      await api.delete(`/incassi-cantiere/${voce.id}`);
      toast.success("Incasso eliminato");
      loadReport();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!cantiere) {
    return <AppShell><Skeleton className="h-64 rounded-xl" /></AppShell>;
  }

  const pieData = report ? [
    {
      name: "Manodopera",
      value: report.costo_manodopera || 0,
    },
    {
      name: "Materiali",
      value: report.costo_materiali || 0,
    },
    {
      name: "Attrezzature",
      value: report.costo_attrezzature || 0,
    },
  ].filter((x) => x.value > 0) : [];

  const importoLavori = Number(cantiere?.budget) || 0;

  const totaleExtra = (cantiere?.lavori_extra || []).reduce(
    (totale, extra) => totale + (Number(extra.importo) || 0),
    0
  );

  const totaleLavoriRendiconto = importoLavori + totaleExtra;
  const totaleIncassatoRendiconto = Number(report?.totale_incassato) || 0;
  const totaleCostiRendiconto =
    (Number(report?.costo_manodopera) || 0) +
    (Number(report?.costo_materiali) || 0) +
    (Number(report?.costo_attrezzature) || 0);
  const daIncassareRendiconto =
    totaleLavoriRendiconto - totaleIncassatoRendiconto;
  const utileRendiconto =
    totaleLavoriRendiconto - totaleCostiRendiconto;

  const percentualeUtile =
    totaleLavoriRendiconto > 0
      ? (utileRendiconto / totaleLavoriRendiconto) * 100
      : 0;

  const datiPercentualeUtile =
    totaleLavoriRendiconto > 0
      ? utileRendiconto >= 0
        ? [
            {
              name: "Costi totali",
              value: Math.min(
                totaleCostiRendiconto,
                totaleLavoriRendiconto
              ),
            },
            {
              name: "Utile lordo",
              value: utileRendiconto,
            },
          ].filter((voce) => voce.value > 0)
        : [
            {
              name: "Costi totali",
              value: totaleCostiRendiconto,
            },
          ]
      : [];
  const filterByLav = (arr, f) =>
    f === "Tutte"
      ? arr
      : arr.filter((x) => (x.lavorazione_id || "__none__") === f);

  const operaiList = [
    ...new Set(mano.map((m) => m.operaio).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "it"));

  const aziendeList = [
    ...new Set(mano.map((m) => m.azienda || m.ruolo).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "it"));

  const manoView = filterByLav(mano, manoLavFilter).filter((m) => {
    const matchOperaio =
      manoOperaioFilter === "Tutti" ||
      m.operaio === manoOperaioFilter;

    const matchAzienda =
      manoAziendaFilter === "Tutte" ||
      (m.azienda || m.ruolo || "") === manoAziendaFilter;

    const matchData = manoDataFilter
      ? m.data === manoDataFilter
      : manoMeseFilter
        ? (m.data || "").startsWith(manoMeseFilter)
        : true;

    return matchOperaio && matchAzienda && matchData;
  });

  const fornitoriList = [
    ...new Set(mat.map((m) => m.fornitore).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, "it"));

  const matView = filterByLav(mat, matLavFilter).filter((m) => {
    const testoRicerca = matSearchFilter.trim().toLocaleLowerCase("it");
    const matchRicerca = !testoRicerca || [
      m.nome,
      m.descrizione,
      m.numero_ddt,
      m.fornitore,
      m.azienda_nome,
      m.azienda,
      lavorazioni.find((lavorazione) => lavorazione.id === m.lavorazione_id)?.nome,
    ].some((valore) => String(valore || "").toLocaleLowerCase("it").includes(testoRicerca));
    const matchFornitore =
      matFornitoreFilter === "Tutti" ||
      (m.fornitore || "") === matFornitoreFilter;

    const matchData = matDataFilter
      ? m.data_acquisto === matDataFilter
      : matMeseFilter
        ? (m.data_acquisto || "").startsWith(matMeseFilter)
        : true;

    return matchRicerca && matchFornitore && matchData;
  });

  return (
    <AppShell><div className="cantiere-scroll-page lg:h-[calc(100dvh-6rem)] lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden"><button data-testid="back-button" onClick={() => navigate("/", { state: location.state || {} })}
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> Tutti i cantieri
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{cantiere.stato}</Badge>
            {cantiere.fuori_budget && (
              <Badge className="border border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-100">Fuori budget</Badge>
            )}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold">{cantiere.nome}</h1>
          <p className="text-muted-foreground mt-2">
            {cantiere.cliente && <span>{cantiere.cliente}</span>}
            {cantiere.cliente && cantiere.indirizzo && <span> - </span>}
            {cantiere.indirizzo}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          {(cantiere.preventivi_collegati || []).map((preventivo) => (
            <Button
              key={preventivo.id}
              variant="outline"
              className="rounded-md font-bold"
              onClick={() => openAttachmentViewer(`${API}/preventivi/${preventivo.id}/pdf`, `Preventivo ${preventivo.numero}`)}
              title={`${preventivo.oggetto || "Preventivo"} · ${eur(preventivo.importo)}`}
            >
              <FileText className="h-4 w-4 mr-2" /> Preventivo {preventivo.numero}
            </Button>
          ))}
          <Button variant="outline" data-testid="edit-cantiere-button" onClick={openEditCantiere}
            className="rounded-md font-bold">
            <Pencil className="h-4 w-4 mr-2" /> Modifica cantiere
          </Button>
        </div>
      </div>

      <Dialog open={incassoOpen} onOpenChange={(open) => {
        setIncassoOpen(open);
        if (!open) resetIncasso();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{incassoEditId ? "Modifica incasso" : "Registra incasso"}</DialogTitle>
            <DialogDescription>
              Registra un pagamento ricevuto per questo cantiere.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={salvaIncasso} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data incasso *</Label>
                <Input
                  type="date"
                  required
                  value={incassoForm.data_incasso}
                  onChange={(e) => setIncassoForm({ ...incassoForm, data_incasso: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Importo (EUR) *</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={incassoForm.importo}
                  onChange={(e) => setIncassoForm({ ...incassoForm, importo: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Azienda che ha ricevuto l'acconto</Label>
              <Select
                value={incassoForm.azienda_id || "__none__"}
                onValueChange={(value) => {
                  const azienda = aziendeAnagrafica.find((x) => x.id === value);
                  setIncassoForm({ ...incassoForm, azienda_id: value === "__none__" ? "" : value, azienda_nome: azienda?.nome || "" });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Non assegnato</SelectItem>
                  {aziendeAnagrafica.map((azienda) => <SelectItem key={azienda.id} value={azienda.id}>{azienda.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Modalità di pagamento</Label>
              <Select
                value={incassoForm.modalita}
                onValueChange={(value) => setIncassoForm({ ...incassoForm, modalita: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonifico">Bonifico</SelectItem>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="assegno">Assegno</SelectItem>
                  <SelectItem value="carta_pos">Carta / POS</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Riferimento</Label>
              <Input
                placeholder="Es. CRO, numero assegno o ricevuta"
                value={incassoForm.riferimento}
                onChange={(e) => setIncassoForm({ ...incassoForm, riferimento: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={incassoForm.note}
                onChange={(e) => setIncassoForm({ ...incassoForm, note: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIncassoOpen(false)}>Annulla</Button>
              <Button type="submit">{incassoEditId ? "Salva modifiche" : "Registra incasso"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Modifica cantiere</DialogTitle></DialogHeader>
          <form onSubmit={saveCantiere} className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Nome cantiere *</Label>
              <Input data-testid="edit-cantiere-nome-input" required value={editForm.nome}
                onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Input data-testid="edit-cantiere-cliente-input" value={editForm.cliente}
                  onChange={(e) => setEditForm({ ...editForm, cliente: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Importo lavori (EUR)</Label>
                <Input data-testid="edit-cantiere-budget-input" type="number" step="0.01" value={editForm.budget}
                  onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Indirizzo</Label>
              <Input
                value={editForm.indirizzo}
                onChange={(e) =>
                  setEditForm({ ...editForm, indirizzo: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Data inizio lavori *</Label>
              <Input
                type="date"
                required
                value={editForm.data_inizio}
                onChange={(e) => setEditForm({ ...editForm, data_inizio: e.target.value })}
                data-testid="edit-cantiere-data-inizio-input"
              />
            </div>

            <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-base font-bold">Lavori extra ({(editForm.lavori_extra || []).length})</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Gli importi extra vengono sommati all'importo lavori.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-md font-bold"
                  onClick={() => {
                    setEditForm((corrente) => ({
                      ...corrente,
                      lavori_extra: [
                        ...(corrente.lavori_extra || []),
                        { descrizione: "", importo: "" },
                      ],
                    }));
                    window.setTimeout(() => {
                      const elenco = lavoriExtraRef.current;
                      if (elenco) elenco.scrollTo({ top: elenco.scrollHeight, behavior: "smooth" });
                    }, 0);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Aggiungi extra
                </Button>
              </div>

              {(editForm.lavori_extra || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  Nessun lavoro extra inserito.
                </p>
              ) : (
                <div ref={lavoriExtraRef} className="space-y-3 max-h-[330px] overflow-y-auto overscroll-contain pr-2">
                  {(editForm.lavori_extra || []).map((extra, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 sm:grid-cols-[1fr_150px_40px] gap-2 items-end"
                    >
                      <div className="space-y-1">
                        <Label className="text-xs">Descrizione</Label>
                        <Input
                          value={extra.descrizione}
                          placeholder="Esempio: demolizione aggiuntiva"
                          onChange={(e) => {
                            const elenco = [...editForm.lavori_extra];
                            elenco[index] = {
                              ...elenco[index],
                              descrizione: e.target.value,
                            };
                            setEditForm({
                              ...editForm,
                              lavori_extra: elenco,
                            });
                          }}
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Importo (EUR)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={extra.importo}
                          placeholder="0,00"
                          onChange={(e) => {
                            const elenco = [...editForm.lavori_extra];
                            elenco[index] = {
                              ...elenco[index],
                              importo: e.target.value,
                            };
                            setEditForm({
                              ...editForm,
                              lavori_extra: elenco,
                            });
                          }}
                        />
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title="Elimina lavoro extra"
                        className="h-10 w-10 text-destructive hover:bg-destructive/10"
                        onClick={() =>
                          setEditForm({
                            ...editForm,
                            lavori_extra: editForm.lavori_extra.filter(
                              (_, posizione) => posizione !== index
                            ),
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2 border-t border-border pt-3">
                <div className="flex justify-between text-sm">
                  <span>Importo lavori</span>
                  <strong>
                    {eur(parseFloat(editForm.budget) || 0)}
                  </strong>
                </div>

                <div className="flex justify-between text-sm">
                  <span>Totale lavori extra</span>
                  <strong>
                    {eur(
                      (editForm.lavori_extra || []).reduce(
                        (totale, extra) =>
                          totale + (parseFloat(extra.importo) || 0),
                        0
                      )
                    )}
                  </strong>
                </div>

                <div className="flex justify-between text-base text-primary">
                  <span className="font-bold">Totale lavori</span>
                  <strong>
                    {eur(
                      (parseFloat(editForm.budget) || 0) +
                      (editForm.lavori_extra || []).reduce(
                        (totale, extra) =>
                          totale + (parseFloat(extra.importo) || 0),
                        0
                      )
                    )}
                  </strong>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={editForm.stato} onValueChange={(v) => setEditForm({ ...editForm, stato: v, completed_at: v === "Completato" ? (editForm.completed_at || format(new Date(), "yyyy-MM-dd")) : "" })}>
                <SelectTrigger data-testid="edit-cantiere-stato-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editForm.stato === "Completato" && (
              <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <Label>Data di completamento *</Label>
                <Input type="date" required value={editForm.completed_at} onChange={(e) => setEditForm({ ...editForm, completed_at: e.target.value })} data-testid="edit-cantiere-completed-at" />
                <p className="text-xs text-emerald-800">Puoi modificare questa data anche successivamente.</p>
              </div>
            )}
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <input
                type="checkbox"
                checked={editForm.fuori_budget}
                onChange={(e) => setEditForm({ ...editForm, fuori_budget: e.target.checked })}
                className="mt-1 h-4 w-4 accent-amber-600"
                data-testid="edit-cantiere-fuori-budget"
              />
              <span>
                <span className="block font-bold text-amber-900">Fuori budget</span>
                <span className="block text-xs text-amber-800">Il cantiere resta consultabile, ma non viene conteggiato in contabilità, rendicontazioni e grafici complessivi.</span>
              </span>
            </label>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea value={editForm.descrizione} onChange={(e) => setEditForm({ ...editForm, descrizione: e.target.value })} rows={2} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="save-edit-cantiere-button" className="rounded-md font-bold">Salva modifiche</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lavDetail} onOpenChange={(open) => !open && setLavDetail(null)}>
  <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
    {lavDetail && (() => {
      const lm = mano.filter(
        (item) => item.lavorazione_id === lavDetail.id
      );

      const lmt = mat.filter(
        (item) => item.lavorazione_id === lavDetail.id
      );

      const lat = attrezzi.filter(
        (item) => item.lavorazione_id === lavDetail.id
      );

      const cMano = lm.reduce(
        (totale, item) =>
          totale +
          (Number(item.ore) || 0) *
          (Number(item.costo_orario) || 0),
        0
      );

      const cMat = lmt.reduce(
        (totale, item) =>
          totale + (Number(item.costo_totale) || 0),
        0
      );

      const cAtt = lat.reduce(
        (totale, item) =>
          totale +
          (Number(item.ore_utilizzate) || 0) *
          (Number(item.costo_orario) || 0),
        0
      );

      return (
        <>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Layers className="h-6 w-6 text-primary" />

              <span>{lavDetail.nome}</span>

              <Badge variant="outline">
                {lavDetail.stato || "In corso"}
              </Badge>
            </DialogTitle>

            {lavDetail.descrizione && (
              <p className="text-sm text-muted-foreground">
                {lavDetail.descrizione}
              </p>
            )}
          </DialogHeader>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 py-2">
            <StatBox
              icon={HardHat}
              label="Manodopera"
              value={eur(cMano)}
              accent
            />

            <StatBox
              icon={Package}
              label="Materiali"
              value={eur(cMat)}
            />

            <StatBox
              icon={Wrench}
              label="Attrezzature"
              value={eur(cAtt)}
            />

            <StatBox
  icon={TrendingUp}
  label="Totale lavori"
  value={eur(totaleLavoriRendiconto)}
  accent
/>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <HardHat className="h-4 w-4 text-primary" />
              Manodopera
            </h4>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs">Operaio</TableHead>
                    <TableHead className="text-xs">Data</TableHead>
                    <TableHead className="text-xs">Ore</TableHead>
                    <TableHead className="text-xs text-right">
                      Totale
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {lm.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-muted-foreground"
                      >
                        Nessuna voce
                      </TableCell>
                    </TableRow>
                  ) : (
                    lm.map((item) => (
                      <TableRow
                        key={item.id}
                        data-testid={`lavdetail-mano-${item.id}`}
                      >
                        <TableCell className="font-medium">
                          {item.operaio || "-"}
                        </TableCell>

                        <TableCell>
                          {item.data
                            ? item.data.split("-").reverse().join("/")
                            : "-"}
                        </TableCell>

                        <TableCell>{item.ore || 0}</TableCell>

                        <TableCell className="text-right font-semibold">
                          {eur(
                            (Number(item.ore) || 0) *
                            (Number(item.costo_orario) || 0)
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Package className="h-4 w-4" />
              Materiali
            </h4>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs">Materiale</TableHead>
                    <TableHead className="text-xs">N. DDT</TableHead>
                    <TableHead className="text-xs">Fornitore</TableHead>
                    <TableHead className="text-xs text-right">
                      Totale
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {lmt.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-muted-foreground"
                      >
                        Nessuna voce
                      </TableCell>
                    </TableRow>
                  ) : (
                    lmt.map((item) => (
                      <TableRow
                        key={item.id}
                        data-testid={`lavdetail-mat-${item.id}`}
                      >
                        <TableCell className="font-medium">
                          {item.nome || item.fornitore || "-"}
                        </TableCell>

                        <TableCell>
                          {item.numero_ddt || "-"}
                        </TableCell>

                        <TableCell className="text-muted-foreground">
                          {item.fornitore || "-"}
                        </TableCell>

                        <TableCell className="text-right font-semibold">
                          {eur(Number(item.costo_totale) || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold flex items-center gap-2 mb-2">
              <Wrench className="h-4 w-4" />
              Attrezzature
            </h4>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-xs">Attrezzo</TableHead>
                    <TableHead className="text-xs">Azienda</TableHead>
                    <TableHead className="text-xs">Data</TableHead>
                    <TableHead className="text-xs">Ore</TableHead>
                    <TableHead className="text-xs">Costo orario</TableHead>
                    <TableHead className="text-xs text-right">
                      Totale
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {lat.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground"
                      >
                        Nessuna attrezzatura
                      </TableCell>
                    </TableRow>
                  ) : (
                    lat.map((item) => (
                      <TableRow
                        key={item.id}
                        data-testid={`lavdetail-attrezzo-${item.id}`}
                      >
                        <TableCell className="font-medium">
                          {item.attrezzo || item.nome || "-"}
                        </TableCell>

                        <TableCell>
                          {item.azienda || "-"}
                        </TableCell>

                        <TableCell>
                          {item.data_utilizzo
                            ? item.data_utilizzo
                                .split("-")
                                .reverse()
                                .join("/")
                            : "-"}
                        </TableCell>

                        <TableCell>
                          {Number(item.ore_utilizzate) || 0}
                        </TableCell>

                        <TableCell>
                          {eur(Number(item.costo_orario) || 0)}
                        </TableCell>

                        <TableCell className="text-right font-semibold">
                          {eur(
                            (Number(item.ore_utilizzate) || 0) *
                            (Number(item.costo_orario) || 0)
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      );
    })()}
  </DialogContent>
</Dialog>

      <Tabs defaultValue="riepilogo" className="w-full lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden">
        <TabsList className="h-auto p-1 bg-muted rounded-lg flex flex-wrap gap-1 w-full justify-start sm:w-auto sm:inline-flex lg:flex-none">
          <TabsTrigger value="riepilogo" data-testid="tab-riepilogo" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 font-semibold">
            <LayoutDashboard className="h-4 w-4 mr-2" /> Riepilogo
          </TabsTrigger>
          <TabsTrigger value="manodopera" data-testid="tab-manodopera" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 font-semibold">
            <HardHat className="h-4 w-4 mr-2" /> Manodopera
          </TabsTrigger>
          <TabsTrigger value="materiali" data-testid="tab-materiali" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 font-semibold">
            <Package className="h-4 w-4 mr-2" /> Materiali
          </TabsTrigger>
          <TabsTrigger value="attrezzi" data-testid="tab-attrezzi" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 font-semibold">
            <Wrench className="h-4 w-4 mr-2" /> Attrezzi
          </TabsTrigger>

          <TabsTrigger value="lavorazioni" data-testid="tab-lavorazioni" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 font-semibold">
            <Layers className="h-4 w-4 mr-2" /> Lavorazioni
          </TabsTrigger>
          <TabsTrigger value="foto" data-testid="tab-foto" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 font-semibold">
            <ImageIcon className="h-4 w-4 mr-2" /> Foto
          </TabsTrigger>
          <TabsTrigger value="rendicontazione" data-testid="tab-rendicontazione" className="rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2 font-semibold">
            <FileBarChart className="h-4 w-4 mr-2" /> Rendicontazione
          </TabsTrigger>
        </TabsList>

        {/* RIEPILOGO */}
        <TabsContent value="riepilogo" className="mt-6 space-y-6 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-2">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            
            <StatBox icon={HardHat} label="Manodopera" value={eur(report?.costo_manodopera)} accent />
            <StatBox icon={Package} label="Materiali" value={eur(report?.costo_materiali)} />
            <StatBox icon={Wrench} label="Attrezzature" value={eur(report?.costo_attrezzature)} />
            <StatBox icon={TrendingUp} label="Costo totale" value={eur(report?.costo_totale)} accent />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            
            <StatBox
              icon={Wallet}
              label="Importo lavori"
              value={eur(cantiere.budget)}
            />
            <StatBox
              icon={Plus}
              label="Lavori extra"
              value={eur(
                (cantiere.lavori_extra || []).reduce(
                  (totale, extra) => totale + (extra.importo || 0),
                  0
                )
              )}
            />
            <StatBox
              icon={TrendingUp}
              label="Totale lavori"
              value={eur(
                (cantiere.budget || 0) +
                (cantiere.lavori_extra || []).reduce(
                  (totale, extra) => totale + (extra.importo || 0),
                  0
                )
              )}
              accent
            />
          </div>
          {cantiere.descrizione && (
            <div className="rounded-xl border border-border bg-card p-6">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Descrizione</p>
              <p className="text-foreground/90">{cantiere.descrizione}</p>
            </div>
          )}
        </TabsContent>

        {/* MANODOPERA */}
        <TabsContent value="manodopera" className="mt-6 lg:data-[state=active]:flex lg:data-[state=active]:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden">
          <div className="flex items-center justify-between mb-4 lg:flex-none">
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl font-bold tracking-tight">
                Presenze operai
              </h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="report-manodopera-pdf"
                onClick={() =>
                  exportSectionPdf(
                    "manodopera",
                    "report_manodopera",
                    manoView
                  )
                }
              >
                <Download className="h-4 w-4 mr-2" />
                Report PDF
              </Button>
            </div>
            <Dialog open={manoOpen} onOpenChange={setManoOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-manodopera-button" onClick={resetMano} className="rounded-md font-bold">
                  <Plus className="h-4 w-4 mr-2" /> Registra presenza
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{manoEditId ? "Modifica presenza" : "Registra presenza"}</DialogTitle></DialogHeader>
                <form onSubmit={addMano} className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Operaio *</Label>
                      <Select
  value={manoForm.operaio_id || "__none__"}
  onValueChange={(value) => {
    const operaio = operaiCatalogo.find(
      (elemento) => elemento.id === value
    );
    const disponibilita = disponibilitaOperai[value] || { ore_disponibili: 8 };

    setManoForm({
      ...manoForm,
      operaio_id:
        value === "__none__" ? null : value,
      operaio: operaio
        ? `${operaio.cognome || ""} ${
            operaio.nome || ""
          }`.trim()
        : "",
      ruolo: operaio?.ruolo || "",
      azienda: operaio?.azienda_nome || "",
      costo_orario: operaio ? costoOperaioAllaData(operaio, manoForm.data) : "",
      ore: operaio ? String(disponibilita.ore_disponibili) : "",
    });
  }}
>
  <SelectTrigger data-testid="mano-operaio-input">
    <SelectValue placeholder="Seleziona operaio" />
  </SelectTrigger>

  <SelectContent>
    <SelectItem value="__none__">
      Seleziona dall'anagrafica
    </SelectItem>

    {operaiDisponibili.map((operaio) => {
      const nomeCompleto =
        `${operaio.cognome || ""} ${
          operaio.nome || ""
        }`.trim();
      const disponibilita = disponibilitaOperai[operaio.id] || { ore_disponibili: 8 };

      return (
        <SelectItem
          key={operaio.id}
          value={operaio.id}
        >
          {nomeCompleto}
          {operaio.azienda_nome
            ? ` - ${operaio.azienda_nome}`
            : ""}
          {Number(disponibilita.ore_disponibili) < 8
            ? ` · disponibili ${disponibilita.ore_disponibili} ore`
            : ""}
        </SelectItem>
      );
    })}
  </SelectContent>
</Select>
                      {disponibilitaOperaiLoading ? (
                        <p className="text-xs text-muted-foreground">Verifica disponibilità in tutti i cantieri...</p>
                      ) : disponibilitaSelezionato ? (
                        <p className="text-xs text-muted-foreground">
                          Già registrate: {disponibilitaSelezionato.ore_utilizzate} ore · disponibili: {disponibilitaSelezionato.ore_disponibili} ore
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label>Azienda</Label>
                      <Input data-testid="mano-azienda-input" value={manoForm.azienda} readOnly placeholder="Seleziona prima l'operaio" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <DatePicker value={manoForm.data} onChange={(d) => {
                      const operaio = operaiCatalogo.find((voce) => voce.id === manoForm.operaio_id);
                      setManoForm({
                        ...manoForm,
                        data: d,
                        costo_orario: operaio ? costoOperaioAllaData(operaio, d) : manoForm.costo_orario,
                      });
                    }} testid="mano-data-picker" max={format(new Date(), "yyyy-MM-dd")} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ore lavorate *</Label>
                      <Input data-testid="mano-ore-input" type="number" min="0.5"
                        max={disponibilitaSelezionato?.ore_disponibili ?? 8} step="0.5" required value={manoForm.ore}
                        onChange={(e) => setManoForm({ ...manoForm, ore: e.target.value })} placeholder="8" />
                    </div>
                    <div className="space-y-2">
                      <Label>Costo orario (EUR) *</Label>
                      <Input data-testid="mano-costo-input" type="number" step="0.01" required value={manoForm.costo_orario}
                        onChange={(e) => setManoForm({ ...manoForm, costo_orario: e.target.value })} placeholder="25" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Lavorazione</Label>
                    <Select value={manoForm.lavorazione_id} onValueChange={(v) => setManoForm({ ...manoForm, lavorazione_id: v })}>
                      <SelectTrigger data-testid="mano-lavorazione-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nessuna lavorazione</SelectItem>
                        {lavorazioni.map((l) => <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <NuovaLavorazioneButton cantiereId={id} onCreated={(nuova) => collegaLavorazioneCreata("manodopera", nuova)} className="w-full" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={disponibilitaOperaiLoading} data-testid="save-manodopera-button" className="rounded-md font-bold">
                      {disponibilitaOperaiLoading ? "Verifica ore..." : "Salva"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 mb-4 lg:flex-none">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4 items-end">

              <div className="space-y-2">
                <Label className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Lavorazione
                </Label>
                <Select value={manoLavFilter} onValueChange={setManoLavFilter}>
                  <SelectTrigger data-testid="mano-lav-filter" className="h-10 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tutte">Tutte le lavorazioni</SelectItem>
                    <SelectItem value="__none__">Senza lavorazione</SelectItem>
                    {lavorazioni.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Operaio
                </Label>
                <Select
                  value={manoOperaioFilter}
                  onValueChange={setManoOperaioFilter}
                >
                  <SelectTrigger data-testid="mano-operaio-filter" className="h-10 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tutti">Tutti gli operai</SelectItem>
                    {operaiList.map((nome) => (
                      <SelectItem key={nome} value={nome}>{nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Azienda
                </Label>
                <Select
                  value={manoAziendaFilter}
                  onValueChange={setManoAziendaFilter}
                >
                  <SelectTrigger data-testid="mano-azienda-filter" className="h-10 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tutte">Tutte le aziende</SelectItem>
                    {aziendeList.map((azienda) => (
                      <SelectItem key={azienda} value={azienda}>{azienda}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Giorno
                </Label>
                <Input
                  type="date"
                  data-testid="mano-data-filter"
                  value={manoDataFilter}
                  onChange={(e) => {
                    setManoDataFilter(e.target.value);
                    if (e.target.value) setManoMeseFilter("");
                  }}
                  className="h-10 rounded-md"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Mese
                </Label>
                <Input
                  type="month"
                  data-testid="mano-mese-filter"
                  value={manoMeseFilter}
                  onChange={(e) => {
                    setManoMeseFilter(e.target.value);
                    if (e.target.value) setManoDataFilter("");
                  }}
                  className="h-10 rounded-md"
                />
              </div>

              <Button
                type="button"
                variant="outline"
                className="h-10 rounded-md font-bold"
                onClick={() => {
                  setManoLavFilter("Tutte");
                  setManoOperaioFilter("Tutti");
                  setManoAziendaFilter("Tutte");
                  setManoDataFilter("");
                  setManoMeseFilter("");
                }}
              >
                Azzera filtri
              </Button>

            </div>

            <p className="text-sm text-muted-foreground mt-4">
              Presenze visualizzate: <strong>{manoView.length}</strong>
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card lg:flex-1 lg:min-h-0 max-h-[55dvh] lg:max-h-none overflow-auto overscroll-contain">
            <Table>
              <TableHeader className="sticky top-0 z-20 bg-card shadow-sm">
  <TableRow className="bg-muted/50">
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Operaio
    </TableHead>

    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Azienda
    </TableHead>

    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Lavorazione
    </TableHead>

    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Data
    </TableHead>

    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">
      Ore
    </TableHead>

    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">
      Costo orario
    </TableHead>

    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">
      Totale
    </TableHead>

    <TableHead className="w-[90px]" />
  </TableRow>
</TableHeader>
              <TableBody>
                {manoView.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Nessuna presenza registrata</TableCell></TableRow>
                ) : manoView.map((m) => (
                  <TableRow key={m.id} data-testid={`manodopera-row-${m.id}`} className="hover:bg-muted/30">
                    <TableCell className="font-semibold">{m.operaio}</TableCell>
                    <TableCell className="text-muted-foreground">{m.azienda || m.ruolo || "-"}</TableCell>
                    <TableCell>{m.lavorazione_id ? <Badge variant="outline" className="border-secondary/30 text-secondary bg-secondary/5">{lavName(m.lavorazione_id)}</Badge> : <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell>{format(new Date(m.data), "dd/MM/yyyy")}</TableCell>
                    <TableCell className="text-right">{m.ore}</TableCell>
                    <TableCell className="text-right">{eur(m.costo_orario)}</TableCell>
                    <TableCell className="text-right font-semibold">{eur(m.ore * m.costo_orario)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`edit-manodopera-${m.id}`} onClick={() => openEditMano(m)}
                          className="h-8 w-8 hover:bg-primary/10 hover:text-primary"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-manodopera-${m.id}`} onClick={() => delMano(m.id)}
                          className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* MATERIALI */}
        <TabsContent value="materiali" className="mt-6 lg:data-[state=active]:flex lg:data-[state=active]:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden">
          <div className="flex items-center justify-between mb-4 lg:flex-none">
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl font-bold tracking-tight">
                Materiali usati
              </h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="report-materiali-pdf"
                onClick={() =>
                  exportSectionPdf(
                    "materiali",
                    "report_materiali",
                    matView
                  )
                }
              >
                <Download className="h-4 w-4 mr-2" />
                Report PDF
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button type="button" variant="outline" className="rounded-md font-bold" onClick={() => {
                setDdtImportFile(null); setDdtPreview(null); setDdtImportOpen(true);
              }}>
                <FileUp className="h-4 w-4 mr-2" /> Importa DDT/PDF o foto
              </Button>
            <Dialog open={matOpen} onOpenChange={setMatOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-materiale-button" onClick={resetMat} className="rounded-md font-bold">
                  <Plus className="h-4 w-4 mr-2" /> Aggiungi materiale
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{matEditId ? "Modifica materiale" : "Aggiungi materiale"}</DialogTitle></DialogHeader>
                <form onSubmit={addMat} className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>Data acquisto *</Label>
                    <DatePicker value={matForm.data_acquisto} onChange={(d) => setMatForm({ ...matForm, data_acquisto: d })} testid="mat-data-picker" max={format(new Date(), "yyyy-MM-dd")} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Numero DDT</Label>
                      <Input data-testid="mat-ddt-input" value={matForm.numero_ddt}
                        onChange={(e) => setMatForm({ ...matForm, numero_ddt: e.target.value })} placeholder="DDT-2026/0123" />
                    </div>
                    <div className="space-y-2">
                      <Label>Costo totale (EUR) *</Label>
                      <Input data-testid="mat-costo-totale-input" type="number" step="0.01" required value={matForm.costo_totale}
                        onChange={(e) => setMatForm({ ...matForm, costo_totale: e.target.value })} placeholder="425.00" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Fornitore</Label>
                    <SearchableCombobox value={matForm.fornitore||"__none__"}
                      onValueChange={(value)=>setMatForm({...matForm,fornitore:value==="__none__"?"":value})}
                      options={[{value:"__none__",label:"Nessun fornitore"},...(matForm.fornitore&&!fornitoriAnagrafica.some(item=>item.nome===matForm.fornitore)?[{value:matForm.fornitore,label:`${matForm.fornitore} (valore storico)`}]:[]),...fornitoriAnagrafica.map(item=>({value:item.nome,label:item.nome}))]}
                      placeholder="Seleziona il fornitore" searchPlaceholder="Scrivi il fornitore..." testId="mat-fornitore-select" />

                    {fornitoriAnagrafica.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Inserisci prima i fornitori nel Gestionale.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Azienda a cui attribuire la spesa *</Label>
                    <Select value={matForm.azienda_id || ""} onValueChange={(value) => {
                      const azienda = aziendeAnagrafica.find((x) => x.id === value);
                      setMatForm({ ...matForm, azienda_id: value, azienda_nome: azienda?.nome || "" });
                    }}>
                      <SelectTrigger data-testid="mat-azienda-select"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                      <SelectContent>
                        {aziendeAnagrafica.map((azienda) => <SelectItem key={azienda.id} value={azienda.id}>{azienda.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">La spesa verrà registrata nei Conti dell’azienda selezionata.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Lavorazione</Label>
                    <Select value={matForm.lavorazione_id} onValueChange={(v) => setMatForm({ ...matForm, lavorazione_id: v })}>
                      <SelectTrigger data-testid="mat-lavorazione-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nessuna lavorazione</SelectItem>
                        {lavorazioni.map((l) => <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <NuovaLavorazioneButton cantiereId={id} onCreated={(nuova) => collegaLavorazioneCreata("materiali", nuova)} className="w-full" />
                  </div>
                  <div className="space-y-2">
                    <Label>Copia bolla (scansione)</Label>
                    {matForm.allegato_path ? (
                      <div className="flex items-center justify-between gap-2 rounded-md border border-border p-2">
                        <button type="button" onClick={() => openAttachmentViewer(bollaUrl(matForm.allegato_path), matForm.allegato_nome || "Bolla")} data-testid="mat-bolla-view" className="text-sm font-medium text-primary hover:underline truncate flex items-center gap-2"><Paperclip className="h-4 w-4" /> {matForm.allegato_nome || "Bolla"}</button>
                        <Button type="button" variant="ghost" size="sm" data-testid="mat-bolla-remove" onClick={() => setMatForm({ ...matForm, allegato_path: null, allegato_nome: null })} className="hover:text-destructive">Rimuovi</Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <label data-testid="mat-bolla-upload" className="flex items-center justify-center gap-2 cursor-pointer rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                          {uploadingBolla ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                          {uploadingBolla ? "Caricamento..." : "Carica file"}
                          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadBolla(e.target.files?.[0])} />
                        </label>
                        <label data-testid="mat-bolla-camera" className="flex items-center justify-center gap-2 cursor-pointer rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                          {uploadingBolla ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                          Scatta foto
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadBolla(e.target.files?.[0])} />
                        </label>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-materiale-button" className="rounded-md font-bold">Salva</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          <Dialog open={ddtImportOpen} onOpenChange={(aperto) => {
            if (!aperto) chiudiImportDDT(); else setDdtImportOpen(true);
          }}>
            <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight">Importa DDT da PDF o foto</DialogTitle>
                <DialogDescription>
                  Ogni DDT genera una sola riga nei Materiali e nel Registro DDT. Se il PDF contiene più bolle, le pagine vengono separate e allegate al DDT corrispondente.
                </DialogDescription>
              </DialogHeader>
              {!ddtPreview ? (
                <div className="space-y-4 pt-2">
                  <div className="rounded-xl border border-dashed p-6">
                    <Label htmlFor="ddt-import-file">Documento da analizzare</Label>
                    <Input id="ddt-import-file" className="mt-2" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setDdtImportFile(event.target.files?.[0] || null)} />
                    <p className="mt-2 text-xs text-muted-foreground">Formati ammessi: PDF, JPG, PNG e WEBP. I doppioni vengono riconosciuti confrontando fornitore, numero DDT e data.</p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={chiudiImportDDT}>Annulla</Button>
                    <Button onClick={analizzaDDT} disabled={!ddtImportFile || ddtAnalyzing}>
                      {ddtAnalyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />} Analizza documento
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="space-y-4 pt-2">
                  {(ddtPreview.righe || []).map((riga, indice) => (
                    <div key={riga.documento_id} className={`rounded-xl border p-4 ${riga.duplicato ? "border-red-200 bg-red-50" : "bg-card"}`}>
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                        <p className="font-bold">DDT {indice + 1} · pagine {(riga.pagine || []).join(", ")}</p>
                        {riga.duplicato && <Badge variant="destructive">Già presente: non sarà importato</Badge>}
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="space-y-2"><Label>Data *</Label><Input type="date" max={format(new Date(), "yyyy-MM-dd")} value={riga.data_acquisto || ""} onChange={(e) => aggiornaRigaDDT(indice, "data_acquisto", e.target.value)} /></div>
                        <div className="space-y-2"><Label>Numero DDT *</Label><Input value={riga.numero_ddt || ""} onChange={(e) => aggiornaRigaDDT(indice, "numero_ddt", e.target.value)} /></div>
                        <div className="space-y-2"><Label>Fornitore *</Label>
                          <SearchableCombobox value={riga.fornitore||""} onValueChange={(v)=>aggiornaRigaDDT(indice,"fornitore",v)} allowCustom
                            options={[...(riga.fornitore&&!fornitoriAnagrafica.some(item=>item.nome===riga.fornitore)?[{value:riga.fornitore,label:`${riga.fornitore} (letto dal documento)`}]:[]),...fornitoriAnagrafica.map(item=>({value:item.nome,label:item.nome}))]}
                            placeholder="Seleziona fornitore" searchPlaceholder="Scrivi il fornitore..." />
                        </div>
                        <div className="space-y-2 sm:col-span-2"><Label>Descrizione materiale *</Label><Input value={riga.nome || ""} onChange={(e) => aggiornaRigaDDT(indice, "nome", e.target.value)} /></div>
                        <div className="space-y-2"><Label>Imponibile (EUR) *</Label><Input type="number" min="0" step="0.01" value={riga.costo_totale ?? ""} onChange={(e) => aggiornaRigaDDT(indice, "costo_totale", e.target.value)} /></div>
                        <div className="space-y-2"><Label>Azienda a cui attribuire la spesa *</Label>
                          <Select value={riga.azienda_id || ""} onValueChange={(value) => {
                            const azienda = aziendeAnagrafica.find((voce) => voce.id === value);
                            aggiornaRigaDDT(indice, "azienda_id", value);
                            aggiornaRigaDDT(indice, "azienda_nome", azienda?.nome || "");
                          }}>
                            <SelectTrigger><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                            <SelectContent>{aziendeAnagrafica.map((azienda) => <SelectItem key={azienda.id} value={azienda.id}>{azienda.nome}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2 sm:col-span-2"><Label>Lavorazione (facoltativa)</Label>
                          <Select value={riga.lavorazione_id || "__none__"} onValueChange={(v) => aggiornaRigaDDT(indice, "lavorazione_id", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__none__">Nessuna lavorazione</SelectItem>{lavorazioni.map((lavorazione) => <SelectItem key={lavorazione.id} value={lavorazione.id}>{lavorazione.nome}</SelectItem>)}</SelectContent>
                          </Select>
                          <NuovaLavorazioneButton cantiereId={id} onCreated={(nuova) => collegaLavorazioneCreata("importazione-ddt", nuova, indice)} className="w-full" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDdtPreview(null)}>Cambia file</Button>
                    <Button onClick={importaDDT} disabled={ddtImporting || !(ddtPreview.righe || []).some((riga) => !riga.duplicato)}>
                      {ddtImporting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Importa DDT nuovi
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <div className="rounded-xl border border-border bg-card p-3 mb-3 lg:flex-none">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-2.5 items-end">

              <div className="space-y-1.5">
                <Label className="text-[10px] tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Ricerca
                </Label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={matSearchFilter} onChange={(e) => setMatSearchFilter(e.target.value)}
                    placeholder="Numero DDT, materiale, fornitore..." className="h-9 rounded-md pl-9 text-sm" data-testid="mat-text-search" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[10px] tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Lavorazione
                </Label>
                <Select value={matLavFilter} onValueChange={setMatLavFilter}>
                  <SelectTrigger data-testid="mat-lav-filter" className="h-9 rounded-md text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tutte">Tutte le lavorazioni</SelectItem>
                    <SelectItem value="__none__">Senza lavorazione</SelectItem>
                    {lavorazioni.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[10px] tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Fornitore
                </Label>
                <SearchableCombobox value={matFornitoreFilter} onValueChange={setMatFornitoreFilter}
                  options={[{value:"Tutti",label:"Tutti i fornitori"},...fornitoriList.map(fornitore=>({value:fornitore,label:fornitore}))]}
                  placeholder="Tutti i fornitori" searchPlaceholder="Scrivi il fornitore..." className="h-9 text-sm" testId="mat-fornitore-filter" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[10px] tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Giorno acquisto
                </Label>
                <Input
                  type="date"
                  data-testid="mat-data-filter"
                  value={matDataFilter}
                  onChange={(e) => {
                    setMatDataFilter(e.target.value);
                    if (e.target.value) setMatMeseFilter("");
                  }}
                  className="h-9 rounded-md text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[10px] tracking-[0.12em] uppercase font-bold text-muted-foreground">
                  Mese acquisto
                </Label>
                <Input
                  type="month"
                  data-testid="mat-mese-filter"
                  value={matMeseFilter}
                  onChange={(e) => {
                    setMatMeseFilter(e.target.value);
                    if (e.target.value) setMatDataFilter("");
                  }}
                  className="h-9 rounded-md text-sm"
                />
              </div>

              <Button
                type="button"
                variant="outline"
                className="h-9 rounded-md px-3 text-sm font-bold"
                onClick={() => {
                  setMatLavFilter("Tutte");
                  setMatFornitoreFilter("Tutti");
                  setMatSearchFilter("");
                  setMatDataFilter("");
                  setMatMeseFilter("");
                }}
              >
                Azzera filtri
              </Button>

            </div>

            <p className="text-xs text-muted-foreground mt-2.5">
              Materiali visualizzati: <strong>{matView.length}</strong>
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card lg:flex-1 lg:min-h-0 max-h-[55dvh] lg:max-h-none overflow-auto overscroll-contain">
            <Table>
              <TableHeader className="sticky top-0 z-20 bg-card shadow-sm">
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Materiale</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Lavorazione</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">N. DDT</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Fornitore</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Azienda</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Data</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Bolla</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">Totale</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matView.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">Nessun materiale registrato</TableCell></TableRow>
                ) : matView.map((m) => (
                  <TableRow key={m.id} data-testid={`materiale-row-${m.id}`} className="hover:bg-muted/30">
                    <TableCell className="font-semibold">{m.nome || m.fornitore || "-"}</TableCell>
                    <TableCell>{m.lavorazione_id ? <Badge variant="outline" className="border-secondary/30 text-secondary bg-secondary/5">{lavName(m.lavorazione_id)}</Badge> : <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell>{m.numero_ddt || <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell className="text-muted-foreground">{m.fornitore || "-"}</TableCell>
                    <TableCell>{m.azienda_nome || <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell>{format(new Date(m.data_acquisto), "dd/MM/yyyy")}</TableCell>
                    <TableCell>{materialeHaBolla(m) ? <button type="button" onClick={() => openAttachmentViewer(materialeBollaUrl(m), m.allegato_nome || (m.magazzino_movement_id ? "Bolla Magazzino" : m.registro_fatture_id ? "Fattura" : "Bolla"))} data-testid={`bolla-view-${m.id}`} className="text-primary hover:underline inline-flex items-center gap-1 text-sm font-medium"><Paperclip className="h-3.5 w-3.5" /> {m.magazzino_movement_id ? "Bolla Magazzino" : m.registro_fatture_id ? "Fattura" : "Bolla"}</button> : <span className="text-muted-foreground">-</span>}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {Number(m.quantita_resa_magazzino || 0) > 0 && (
                        <span
                          className="block text-base font-bold leading-none text-emerald-600"
                          title={`Reso al magazzino: ${Number(m.quantita_resa_magazzino).toLocaleString("it-IT")} ${m.unita || ""}`}
                          aria-label={`Reso al magazzino: ${Number(m.quantita_resa_magazzino).toLocaleString("it-IT")} ${m.unita || ""}`}
                        >
                          *
                        </span>
                      )}
                      {eur(m.costo_totale)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`edit-materiale-${m.id}`} onClick={() => openEditMat(m)}
                          className="h-8 w-8 hover:bg-primary/10 hover:text-primary"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-materiale-${m.id}`} onClick={() => delMat(m.id)}
                          className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ATTREZZI */}
        <TabsContent value="attrezzi" className="mt-6 lg:data-[state=active]:flex lg:data-[state=active]:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden">

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-heading text-2xl font-bold tracking-tight">
                  Attrezzi utilizzati
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="report-attrezzi-pdf"
                  onClick={() =>
                    exportSectionPdf(
                      "attrezzi",
                      "report_attrezzature",
                      attrezziView
                    )
                  }
                >
                  <Download className="h-4 w-4 mr-2" />
                  Report PDF
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Registra attrezzatura, azienda, ore e costo orario.
              </p>
            </div>

            <Dialog open={attrezzoOpen} onOpenChange={setAttrezzoOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-attrezzo-button"
                  onClick={resetAttrezzo}
                  className="rounded-md font-bold"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Aggiungi attrezzo
                </Button>
              </DialogTrigger>

              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl tracking-tight">
                    {attrezzoEditId
                      ? "Modifica attrezzo"
                      : "Aggiungi attrezzo"}
                  </DialogTitle>
                </DialogHeader>

                                <form
                  onSubmit={saveAttrezzo}
                  className="space-y-4 pt-2"
                >
                  <div className="space-y-2">
                    <Label>Seleziona attrezzo *</Label>

                    {catalogoAttrezzi.length === 0 ? (
                      <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
                        Nessun attrezzo presente nel catalogo.
                        Vai nelle Impostazioni generali per inserirlo.
                      </div>
                    ) : (
                      <Select
                        value={attrezzoForm.catalogo_id}
                        onValueChange={(value) =>
                          setAttrezzoForm({
                            ...attrezzoForm,
                            catalogo_id: value,
                          })
                        }
                      >
                        <SelectTrigger data-testid="attrezzo-catalogo-select">
                          <SelectValue placeholder="Scegli un attrezzo" />
                        </SelectTrigger>

                        <SelectContent>
                          {catalogoAttrezzi.map((item) => (
  <SelectItem
    key={item.id}
    value={item.id}
  >
    {item.nome}
    {[item.marca, item.modello, item.targa]
      .filter(Boolean).length > 0
      ? ` - ${[item.marca, item.modello, item.targa]
          .filter(Boolean)
          .join(" - ")}`
      : ""}
  </SelectItem>
))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {attrezzoSelezionato && (
                    <div className="rounded-lg border border-border bg-muted/30 p-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Marca
                          </p>
                          <p className="font-semibold">
                            {attrezzoSelezionato.marca || "-"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-muted-foreground">
                            Modello
                          </p>
                          <p className="font-semibold">
                            {attrezzoSelezionato.modello || "-"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-muted-foreground">
                            Targa
                          </p>
                          <p className="font-semibold">
                            {attrezzoSelezionato.targa || "-"}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-muted-foreground">
                            Azienda
                          </p>
                          <p className="font-semibold">
                            {attrezzoSelezionato.azienda || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Azienda a cui attribuire la spesa *</Label>
                    <Select
                      value={attrezzoForm.azienda_id || ""}
                      onValueChange={(value) => {
                        const azienda = aziendeAnagrafica.find((item) => item.id === value);
                        setAttrezzoForm({
                          ...attrezzoForm,
                          azienda_id: value,
                          azienda_nome: azienda?.nome || "",
                        });
                      }}
                    >
                      <SelectTrigger data-testid="attrezzo-azienda-spesa-select">
                        <SelectValue placeholder="Seleziona azienda" />
                      </SelectTrigger>
                      <SelectContent>
                        {aziendeAnagrafica.map((azienda) => (
                          <SelectItem key={azienda.id} value={azienda.id}>{azienda.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Il costo di utilizzo verrà registrato nei Conti dell’azienda selezionata.</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Data di utilizzo *</Label>
                    <Input
                      required
                      type="date"
                      max={format(new Date(), "yyyy-MM-dd")}
                      data-testid="attrezzo-data-input"
                      value={attrezzoForm.data_utilizzo}
                      onChange={(e) =>
                        setAttrezzoForm({
                          ...attrezzoForm,
                          data_utilizzo: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lavorazione</Label>
                    <Select
                      value={attrezzoForm.lavorazione_id}
                      onValueChange={(value) =>
                        setAttrezzoForm({
                          ...attrezzoForm,
                          lavorazione_id: value,
                        })
                      }
                    >
                      <SelectTrigger data-testid="attrezzo-lavorazione-select">
                        <SelectValue placeholder="Seleziona una lavorazione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          Nessuna lavorazione
                        </SelectItem>
                        {lavorazioni.map((lavorazione) => (
                          <SelectItem
                            key={lavorazione.id}
                            value={lavorazione.id}
                          >
                            {lavorazione.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <NuovaLavorazioneButton cantiereId={id} onCreated={(nuova) => collegaLavorazioneCreata("attrezzature", nuova)} className="w-full" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ore utilizzate *</Label>
                      <Input
                        required
                        type="number"
                        min="0"
                        step="0.5"
                        data-testid="attrezzo-ore-input"
                        value={attrezzoForm.ore_utilizzate}
                        onChange={(e) =>
                          setAttrezzoForm({
                            ...attrezzoForm,
                            ore_utilizzate: e.target.value,
                          })
                        }
                        placeholder="8"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Costo orario</Label>
                      <Input
                        readOnly
                        value={
                          attrezzoSelezionato
                            ? eur(attrezzoSelezionato.costo_orario)
                            : ""
                        }
                        placeholder="Automatico"
                        className="bg-muted"
                      />
                    </div>
                  </div>

                  <div className="rounded-md bg-muted p-3 flex justify-between">
                    <span className="font-semibold">
                      Totale
                    </span>
                    <strong className="text-primary">
                      {eur(
                        (parseFloat(attrezzoForm.ore_utilizzate) || 0) *
                        (attrezzoSelezionato?.costo_orario || 0)
                      )}
                    </strong>
                  </div>

                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={catalogoAttrezzi.length === 0}
                      data-testid="save-attrezzo-button"
                      className="rounded-md font-bold"
                    >
                      Salva
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="rounded-xl border border-border bg-card p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 items-end">

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                  Attrezzature
                </Label>
                <Select
                  value={attrezzoNomeFilter}
                  onValueChange={setAttrezzoNomeFilter}
                >
                  <SelectTrigger data-testid="attrezzo-nome-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tutti">
                      Tutte le attrezzature
                    </SelectItem>
                    {attrezzatureUsate.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.nome}
                        {item.targa ? ` - ${item.targa}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                  Azienda
                </Label>
                <Select
                  value={attrezzoAziendaFilter}
                  onValueChange={setAttrezzoAziendaFilter}
                >
                  <SelectTrigger data-testid="attrezzo-azienda-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tutte">
                      Tutte le aziende
                    </SelectItem>
                    {aziendeAttrezziFilter.map((azienda) => (
                      <SelectItem key={azienda} value={azienda}>
                        {azienda}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                  Giorno
                </Label>
                <Input
                  type="date"
                  value={attrezzoGiornoFilter}
                  onChange={(e) => {
                    setAttrezzoGiornoFilter(e.target.value);
                    if (e.target.value) {
                      setAttrezzoMeseFilter("");
                    }
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                  Mese
                </Label>
                <Input
                  type="month"
                  value={attrezzoMeseFilter}
                  onChange={(e) => {
                    setAttrezzoMeseFilter(e.target.value);
                    if (e.target.value) {
                      setAttrezzoGiornoFilter("");
                    }
                  }}
                />
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAttrezzoNomeFilter("Tutti");
                  setAttrezzoAziendaFilter("Tutte");
                  setAttrezzoGiornoFilter("");
                  setAttrezzoMeseFilter("");
                }}
                className="font-bold"
              >
                Azzera filtri
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mt-4">
              Utilizzi visualizzati:
              <strong className="ml-1">
                {attrezziView.length}
              </strong>
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 lg:flex-none">
            <StatBox
              icon={Wrench}
              label="Attrezzi registrati"
              value={attrezzi.length}
            />
            <StatBox
              icon={TrendingUp}
              label="Costo totale attrezzi"
              value={eur(costoTotaleAttrezzi)}
              accent
            />
          </div>

          <div className="rounded-xl border border-border bg-card lg:flex-1 lg:min-h-0 max-h-[55dvh] lg:max-h-none overflow-auto overscroll-contain">
            <Table>
              <TableHeader className="sticky top-0 z-20 bg-card shadow-sm">
  <TableRow className="bg-muted/50">
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Attrezzo
    </TableHead>
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Azienda
    </TableHead>
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Lavorazione
    </TableHead>
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">
      Data
    </TableHead>
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">
      Ore utilizzate
    </TableHead>
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">
      Costo orario
    </TableHead>
    <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">
      Totale
    </TableHead>
    <TableHead className="w-[90px]" />
  </TableRow>
</TableHeader>

<TableBody>
  {attrezziView.length === 0 ? (
    <TableRow>
      <TableCell
        colSpan={8}
        className="text-center py-12 text-muted-foreground"
      >
        Nessun attrezzo registrato
      </TableCell>
    </TableRow>
  ) : (
    attrezziView.map((item) => (
      <TableRow
        key={item.id}
        data-testid={`attrezzo-row-${item.id}`}
        className="hover:bg-muted/30"
      >
        <TableCell>
          <p className="font-semibold">
            {item.attrezzo || "-"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {[item.marca, item.modello]
              .filter(Boolean)
              .join(" - ") || "-"}
            {item.targa ? ` - ${item.targa}` : ""}
          </p>
        </TableCell>

        <TableCell className="text-muted-foreground">
          {item.azienda || "-"}
        </TableCell>

        <TableCell>
          {item.lavorazione_id ? (
            <Badge
              variant="outline"
              className="border-secondary/30 text-secondary bg-secondary/5"
            >
              {lavName(item.lavorazione_id)}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>

        <TableCell>
          {item.data_utilizzo
            ? item.data_utilizzo.split("-").reverse().join("/")
            : "-"}
        </TableCell>

        <TableCell className="text-right tabular-nums">
          {Number(item.ore_utilizzate || 0).toLocaleString("it-IT", {
            maximumFractionDigits: 2,
          })}
        </TableCell>

        <TableCell className="text-right tabular-nums">
          {eur(item.costo_orario)}
        </TableCell>

        <TableCell className="text-right font-semibold tabular-nums">
          {eur(
            (Number(item.ore_utilizzate) || 0) *
            (Number(item.costo_orario) || 0)
          )}
        </TableCell>

        <TableCell>
          <div className="flex items-center justify-end gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid={`edit-attrezzo-${item.id}`}
              onClick={() => openEditAttrezzo(item)}
              className="h-8 w-8 hover:bg-primary/10 hover:text-primary"
              title="Modifica"
            >
              <Pencil className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid={`delete-attrezzo-${item.id}`}
              onClick={() => deleteAttrezzo(item)}
              className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive"
              title="Elimina"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    ))
  )}
</TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* LAVORAZIONI */}
        <TabsContent value="lavorazioni" className="mt-6 lg:data-[state=active]:flex lg:data-[state=active]:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden">
          <div className="flex items-center justify-between mb-4 lg:flex-none">
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Lavorazioni</h2>
              <p className="text-muted-foreground text-sm mt-1">Raggruppa manodopera e materiali per singola lavorazione.</p>
            </div>
            <Dialog open={lavOpen} onOpenChange={setLavOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-lavorazione-button" onClick={resetLav} className="rounded-md font-bold">
                  <Plus className="h-4 w-4 mr-2" /> Nuova lavorazione
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">{lavEditId ? "Modifica lavorazione" : "Nuova lavorazione"}</DialogTitle></DialogHeader>
                <form onSubmit={saveLav} className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input data-testid="lav-nome-input" required value={lavForm.nome}
                      onChange={(e) => setLavForm({ ...lavForm, nome: e.target.value })} placeholder="Fondazioni" />
                  </div>
                  <div className="space-y-2">
                    <Label>Descrizione</Label>
                    <Textarea value={lavForm.descrizione} onChange={(e) => setLavForm({ ...lavForm, descrizione: e.target.value })} rows={2} placeholder="Scavo e getto fondazioni" />
                  </div>
                  <div className="space-y-2">
                    <Label>Stato</Label>
                    <Select value={lavForm.stato} onValueChange={(v) => setLavForm({ ...lavForm, stato: v })}>
                      <SelectTrigger data-testid="lav-stato-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="In corso">In corso</SelectItem>
                        <SelectItem value="Completato">Completato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-lavorazione-button" className="rounded-md font-bold">Salva</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {lavorazioni.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card p-16 text-center">
              <Layers className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <p className="font-heading text-xl font-semibold mt-4">Nessuna lavorazione</p>
              <p className="text-muted-foreground mt-1">Crea una lavorazione per raggruppare le voci.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:pr-2 lg:content-start">
              {lavorazioni.map((l) => (
                <div key={l.id} data-testid={`lavorazione-card-${l.id}`} className="rounded-xl border border-border bg-card p-6">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-heading text-lg font-bold tracking-tight">{l.nome}</h3>
                    <Badge variant="outline" className={l.stato === "Completato" ? "border-emerald-500/30 text-emerald-600 bg-emerald-500/10" : "border-primary/30 text-primary bg-primary/10"}>{l.stato}</Badge>
                  </div>
                  {l.descrizione && <p className="text-sm text-muted-foreground mb-4">{l.descrizione}</p>}
                  <div className="space-y-1.5 pt-3 border-t border-border">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Manodopera</span><span className="font-semibold">{eur(l.costo_manodopera)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Materiali</span><span className="font-semibold">{eur(l.costo_materiali)}</span></div>
                    <div className="flex justify-between items-center pt-1"><span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Totale</span><span className="font-heading text-lg font-bold text-primary">{eur(l.costo_totale)}</span></div>
                  </div>
                  <div className="flex items-center gap-1 mt-4">
                    <Button variant="default" size="sm" data-testid={`view-lavorazione-${l.id}`} onClick={() => setLavDetail(l)} className="rounded-md"><Eye className="h-3.5 w-3.5 mr-1" /> Dettaglio</Button>
                    <Button variant="outline" size="sm" data-testid={`edit-lavorazione-${l.id}`} onClick={() => openEditLav(l)} className="rounded-md"><Pencil className="h-3.5 w-3.5 mr-1" /> Modifica</Button>
                    <Button variant="ghost" size="sm" data-testid={`delete-lavorazione-${l.id}`} onClick={() => delLav(l.id)} className="rounded-md hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* FOTO */}
        <TabsContent value="foto" className="mt-6 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight">Foto cantiere</h2>
              <p className="text-muted-foreground text-sm mt-1">Scatta o carica foto di avanzamento lavori dal telefono.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {foto.length > 0 && <>
                <Button type="button" variant="outline" onClick={() => setFotoSelezionate(fotoSelezionate.length === foto.length ? [] : foto.map((item) => item.id))}>
                  {fotoSelezionate.length === foto.length ? "Deseleziona tutte" : "Seleziona tutte"}
                </Button>
                <Button type="button" variant="outline" disabled={!fotoSelezionate.length || scaricandoFoto} onClick={scaricaFotoSelezionate}>
                  {scaricandoFoto ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileArchive className="h-4 w-4 mr-2" />}
                  {fotoSelezionate.length > 1 ? `Scarica ZIP (${fotoSelezionate.length})` : "Scarica foto"}
                </Button>
              </>}
              <label data-testid="foto-camera" className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-primary text-primary-foreground font-bold px-4 h-11 transition-transform active:scale-[0.98]">
                {uploadingFoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Scatta foto
                <input type="file" accept="image/*" capture="environment" multiple disabled={uploadingFoto} className="hidden" onChange={(e) => { uploadFoto(e.target.files); e.target.value = ""; }} />
              </label>
              <label data-testid="foto-upload" className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-border font-bold px-4 h-11 hover:border-primary hover:text-primary transition-colors">
                <Upload className="h-4 w-4" /> Carica
                <input type="file" accept="image/*" multiple disabled={uploadingFoto} className="hidden" onChange={(e) => { uploadFoto(e.target.files); e.target.value = ""; }} />
              </label>
            </div>
          </div>
          {foto.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card p-16 text-center">
              <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground/40" />
              <p className="font-heading text-xl font-semibold mt-4">Nessuna foto</p>
              <p className="text-muted-foreground mt-1">Aggiungi la prima foto del cantiere.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {foto.map((f) => (
                <div key={f.id} data-testid={`foto-card-${f.id}`} className="group relative rounded-xl overflow-hidden border border-border bg-card aspect-square">
                  <label className="absolute left-2 top-2 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-white/95 shadow">
                    <input type="checkbox" className="h-4 w-4 accent-primary" checked={fotoSelezionate.includes(f.id)} onChange={() => setFotoSelezionate((correnti) => correnti.includes(f.id) ? correnti.filter((fid) => fid !== f.id) : [...correnti, f.id])} aria-label={`Seleziona ${f.nome || "foto"}`} />
                  </label>
                  <button type="button" className="h-full w-full" onClick={() => openAttachmentViewer(bollaUrl(f.path), f.nome || "Foto cantiere")}>
                    <img src={`${API}/foto/${f.id}/thumbnail`} loading="lazy" decoding="async" alt={f.nome || "Foto"} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  </button>
                  <button data-testid={`foto-delete-${f.id}`} onClick={() => delFoto(f.id)}
                    className="absolute top-2 right-2 h-8 w-8 rounded-md bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 pointer-events-none">
                    <p className="text-white text-xs truncate">{format(new Date(f.created_at), "dd/MM/yyyy HH:mm")}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* RENDICONTAZIONE */}
        <TabsContent value="rendicontazione" className="mt-6 space-y-6 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-2">
          <div className="rounded-xl border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex-1 space-y-2">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Rendiconto alla data</Label>
              <div className="max-w-xs">
                <DatePicker value={reportDate} onChange={setReportDate} testid="report-date-picker" placeholder="Tutte le date" />
              </div>
              <p className="text-xs text-muted-foreground">Mostra i costi accumulati fino alla data selezionata.</p>
            </div>
            <Button onClick={exportPdf} disabled={downloading} data-testid="export-pdf-button"
              className="rounded-md font-bold h-11 px-6 transition-transform active:scale-[0.98]">
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Esporta PDF
            </Button>
            <Button onClick={nuovoIncasso} data-testid="register-payment-button"
              className="rounded-md font-bold h-11 px-6 transition-transform active:scale-[0.98]">
              <Plus className="h-4 w-4 mr-2" />
              Registra incasso
            </Button>
            <Button onClick={exportExcel} disabled={downloadingXls} data-testid="export-excel-button" variant="outline"
              className="rounded-md font-bold h-11 px-6 transition-transform active:scale-[0.98]">
              {downloadingXls ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              Esporta Excel
            </Button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            
            <StatBox icon={HardHat} label="Manodopera" value={eur(report?.costo_manodopera)} accent />
            <StatBox icon={Package} label="Materiali" value={eur(report?.costo_materiali)} />
            <StatBox icon={Wrench} label="Attrezzature" value={eur(report?.costo_attrezzature)} />
            <StatBox
  icon={TrendingUp}
  label="Totale lavori"
  value={eur(totaleLavoriRendiconto)}
  accent
/>
            <StatBox icon={Wallet} label="Incassato" value={eur(totaleIncassatoRendiconto)} accent />
            <StatBox
              icon={FileBarChart}
              label={daIncassareRendiconto >= 0 ? "Da incassare" : "Incasso eccedente"}
              value={eur(Math.abs(daIncassareRendiconto))}
              accent={daIncassareRendiconto !== 0}
            />
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="p-6 pb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-semibold">Storico incassi</h3>
                <p className="text-sm text-muted-foreground">Pagamenti ricevuti fino alla data del rendiconto.</p>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Data</TableHead>
                  <TableHead>Modalità</TableHead>
                  <TableHead>Riferimento</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead className="w-24 text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.incassi || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                      Nessun incasso registrato
                    </TableCell>
                  </TableRow>
                ) : report.incassi.map((voce) => (
                  <TableRow key={voce.id}>
                    <TableCell>{(voce.data_incasso || "").split("-").reverse().join("/")}</TableCell>
                    <TableCell className="capitalize">{(voce.modalita || "-").replace("_", " / ")}</TableCell>
                    <TableCell>{voce.riferimento || "-"}</TableCell>
                    <TableCell className="max-w-xs whitespace-normal break-words">{voce.note || "-"}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">{eur(voce.importo)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" title="Modifica incasso" onClick={() => modificaIncasso(voce)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Elimina incasso" className="text-destructive" onClick={() => eliminaIncasso(voce)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-heading text-lg font-semibold mb-4">Ripartizione costi</h3>
              {pieData.length === 0 ? (
                <p className="text-muted-foreground text-center py-16">Nessun dato disponibile</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={3}>
                      {pieData.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => eur(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card p-6">
  <h3 className="font-heading text-lg font-semibold mb-4">
    Percentuale utili
  </h3>

  {totaleLavoriRendiconto <= 0 ? (
    <p className="text-muted-foreground text-center py-16">
      Inserisci l'importo lavori per calcolare la percentuale
    </p>
  ) : (
    <div className="relative">
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={datiPercentualeUtile}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={100}
            innerRadius={68}
            paddingAngle={3}
            startAngle={90}
            endAngle={-270}
          >
            {datiPercentualeUtile.map((voce) => (
              <Cell
                key={voce.name}
                fill={
                  voce.name === "Utile lordo"
                    ? "#10B981"
                    : utileRendiconto < 0
                      ? "#EF4444"
                      : "#FF5A00"
                }
              />
            ))}
          </Pie>

          <Tooltip
            formatter={(valore) => eur(valore)}
            contentStyle={{
              borderRadius: 8,
              border: "1px solid hsl(var(--border))",
              background: "hsl(var(--card))",
            }}
          />

          <Legend />
        </PieChart>
      </ResponsiveContainer>

      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span
          className={`font-heading text-3xl font-bold ${
            percentualeUtile >= 0
              ? "text-emerald-600"
              : "text-red-600"
          }`}
        >
          {percentualeUtile.toFixed(1)}%
        </span>

        <span className="text-sm text-muted-foreground">
          {percentualeUtile >= 0 ? "Utile lordo" : "Perdita"}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-3 text-center">
        <div className="rounded-lg bg-muted/50 p-3">
          <p className="text-xs uppercase text-muted-foreground">
            Totale lavori
          </p>
          <p className="font-semibold">
            {eur(totaleLavoriRendiconto)}
          </p>
        </div>

        <div className="rounded-lg bg-muted/50 p-3">
          <p className="text-xs uppercase text-muted-foreground">
            Costi totali
          </p>
          <p className="font-semibold">
            {eur(totaleCostiRendiconto)}
          </p>
        </div>

        <div className="rounded-lg bg-muted/50 p-3">
          <p className="text-xs uppercase text-muted-foreground">
            {utileRendiconto >= 0 ? "Utile lordo" : "Perdita"}
          </p>
          <p
            className={`font-semibold ${
              utileRendiconto >= 0
                ? "text-emerald-600"
                : "text-red-600"
            }`}
          >
            {eur(Math.abs(utileRendiconto))}
          </p>
        </div>
      </div>
    </div>
  )}
</div>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="p-6 pb-2"><h3 className="font-heading text-lg font-semibold">Riepilogo per lavorazione</h3></div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold">Lavorazione</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">Manodopera</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">Materiali</TableHead>
                  <TableHead className="uppercase text-xs tracking-[0.1em] font-bold text-right">Totale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.per_lavorazione || []).length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">Nessun dato disponibile</TableCell></TableRow>
                ) : report.per_lavorazione.map((g) => (
                  <TableRow key={g.lavorazione_id || "none"} data-testid="report-lav-row" className="hover:bg-muted/30">
                    <TableCell className="font-semibold">{g.nome}</TableCell>
                    <TableCell className="text-right">{eur(g.manodopera)}</TableCell>
                    <TableCell className="text-right">{eur(g.materiali)}</TableCell>
                    <TableCell className="text-right font-bold text-primary">{eur(g.totale)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs></div></AppShell>
  );
}


